import React, { useState } from 'react';
import tw, { styled } from 'twin.macro';
import { motion } from 'framer-motion';

const initialAgentGreeting = {
  sender: 'agent',
  text: 'Hello! I am your fashion-buying assistant. What would you like help with today?'
};

const samplePlan = [
  'Choose brands you are interested in.',
  'Decide on fashion looks or styles to focus on.',
  'Add products to your shopping list.',
  'Source suppliers for selected products.',
  'Review and finalize your purchase plan.'
];

const workflows = [
  {
    key: 'fetch-news',
    title: 'Fetch Latest Fashion News',
    description: 'Retrieve and display the latest industry news and fashion updates, tailored to your interests.',
    endpoint: '/ws/agent/fetch-news'
  },
  {
    key: 'stock-in-out',
    title: 'Calculate Stock In/Out',
    description: 'Display current stock levels, including low stock, out-of-stock, and restocked items.'
  },
  {
    key: 'revenue-summary',
    title: 'Calculate Total Revenue for the Day',
    description: 'Display the total sales revenue, including transactions, discounts, and refunds for the day.'
  },
  {
    key: 'daily-schedule',
    title: "Review Fashion Buyer's Daily Schedule",
    description: 'Fetch and display your scheduled tasks, meetings, and appointments for the day.'
  },
  {
    key: 'best-sellers',
    title: 'Recalculate Best-Selling Products',
    description: 'Identify and display the best-selling products based on store data.'
  },
  {
    key: 'restock-requests',
    title: 'Send Restock Requests',
    description: 'Automatically generate and send restock requests for low-stock items to suppliers.'
  },
  {
    key: 'customer-feedback',
    title: 'Review Customer Feedback',
    description: 'Display and analyze customer reviews for products.'
  },
  {
    key: 'sourcing-plan',
    title: 'Create New Product Sourcing Plan',
    description: 'Automatically create a sourcing plan for new products based on market trends and demand.'
  },
  {
    key: 'promotions',
    title: 'Track Promotions and Discounts',
    description: 'Track the performance of ongoing sales, discounts, and promotions.'
  },
  {
    key: 'industry-trends',
    title: 'Analyze Fashion Industry Trends',
    description: 'Display insights and analysis on current fashion trends, colors, and materials.'
  }
];

const mockResults = {
  'fetch-news': '📰 Latest News: Paris Fashion Week highlights, new sustainable brands, and more.',
  'stock-in-out': '📦 Stock Levels: 5 low-stock items, 2 out-of-stock, 10 restocked today.',
  'revenue-summary': '💰 Total Revenue Today: $12,340 (5 refunds, $200 in discounts applied).',
  'daily-schedule': '📅 Today: 2 meetings, 1 supplier call, 3 tasks scheduled.',
  'best-sellers': '🏆 Best Sellers: Denim Jacket, White Sneakers, Floral Dress.',
  'restock-requests': '✅ Restock requests sent for: White Sneakers, Black T-Shirts.',
  'customer-feedback': '⭐ Customer Feedback: 4.7/5 average rating, 3 new reviews today.',
  'sourcing-plan': '📝 Sourcing Plan: Focus on eco-friendly fabrics and trending colors (sage green, pastel blue).',
  'promotions': '📊 Promotions: Summer Sale - 120 redemptions, 15% conversion rate.',
  'industry-trends': '📈 Trends: Rise in demand for oversized blazers and sustainable materials.'
};

// Styled components using twin.macro
const WorkflowGrid = styled.div`
  ${tw`grid w-full h-full gap-4`}
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  grid-auto-rows: 1fr;
  min-height: 300px;
  height: 100%;
`;

const Card = styled(motion.div)`
  ${tw`p-4 rounded-lg bg-white dark:bg-gray-800 shadow transition cursor-pointer flex flex-col justify-between border border-gray-100 dark:border-gray-800`}
  min-height: 170px;
  height: 100%;
`;

const RunButton = styled.button`
  ${tw`bg-blue-600 text-white px-4 py-1.5 rounded font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 w-full sm:w-auto`}
`;

const ModalBg = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50`}
`;
const ModalBox = styled.div`
  ${tw`bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 w-full max-w-lg`}
`;

export default function Agent() {
  const [messages, setMessages] = useState([initialAgentGreeting]);
  const [input, setInput] = useState('');
  const [plan, setPlan] = useState(samplePlan.map((step, i) => ({ text: step, done: false })));
  const [running, setRunning] = useState({});
  const [results, setResults] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = { sender: 'user', text: input };
    setMessages(msgs => [...msgs, userMsg]);
    setTimeout(() => {
      setMessages(msgs => [...msgs, { sender: 'agent', text: 'Here is a workflow plan for you:', plan: true }]);
    }, 500);
    setInput('');
  };

  const markStepDone = idx => {
    setPlan(plan => plan.map((step, i) => i === idx ? { ...step, done: !step.done } : step));
  };

  const runWorkflow = async (wf) => {
    if (!wf.endpoint) return;
    setRunning(r => ({ ...r, [wf.key]: true }));
    setResults(r => ({ ...r, [wf.key]: null }));
    try {
      const res = await fetch(wf.endpoint, { method: 'GET' });
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await res.json();
        setResults(r => ({ ...r, [wf.key]: data.result || JSON.stringify(data) }));
      } else {
        const text = await res.text();
        setResults(r => ({ ...r, [wf.key]: 'Error: Unexpected response: ' + (typeof text === 'string' ? text.slice(0, 200) : '') }));
      }
    } catch (err) {
      setResults(r => ({ ...r, [wf.key]: 'Error: ' + err.message }));
    }
    setRunning(r => ({ ...r, [wf.key]: false }));
  };

  const runFetchNews = () => {
    setModalOpen(true);
    setPlan([]);
    setLogs([]);
    setResult('');
    setError('');
    setRunning(true);
    const ws = new window.WebSocket(`ws://${window.location.host}/ws/agent/fetch-news`);
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'plan') {
        setPlan(Array.isArray(msg.content) ? msg.content : [msg.content]);
      } else if (msg.type === 'progress') {
        setLogs(logs => [...logs, msg.content]);
      } else if (msg.type === 'result') {
        setResult(msg.content);
        setRunning(false);
        ws.close();
      } else if (msg.type === 'error') {
        setError(msg.content);
        setRunning(false);
        ws.close();
      }
    };
    ws.onerror = () => {
      setError('WebSocket error');
      setRunning(false);
    };
    ws.onclose = () => setRunning(false);
  };

  return (
    <div tw="max-w-7xl mx-auto p-4 flex flex-col h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow">
      <h1 tw="text-2xl font-bold mb-4">Agent Assistant</h1>
      <section tw="mb-6 flex-1 flex flex-col">
        <h2 tw="text-lg font-semibold mb-2">Daily Workflows</h2>
        <WorkflowGrid>
          {workflows.map((wf, idx) => (
            <Card
              key={wf.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.03, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
            >
              <div tw="mb-2">
                <div tw="font-medium text-gray-900 dark:text-gray-100 text-lg mb-1">{wf.title}</div>
                <div tw="text-sm text-gray-500 dark:text-gray-400">{wf.description}</div>
              </div>
              {wf.key === 'fetch-news' && (
                <div tw="flex flex-col items-end gap-2 mt-auto">
                  <RunButton
                    onClick={runFetchNews}
                    disabled={running[wf.key]}
                  >
                    {running[wf.key] ? (
                      <span tw="flex items-center gap-2">
                        <svg tw="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg> Running...
                      </span>
                    ) : 'Run'}
                  </RunButton>
                  {results[wf.key] && (
                    <div tw="mt-1 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900 rounded px-2 py-1 w-full">{results[wf.key]}</div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </WorkflowGrid>
      </section>
      {modalOpen && (
        <ModalBg>
          <ModalBox>
            <h2 tw="text-xl font-bold mb-2">LLM Plan</h2>
            <ul tw="mb-4 list-disc list-inside text-gray-700 dark:text-gray-200">
              {plan.map((step, i) => <li key={i}>{step}</li>)}
            </ul>
            <h3 tw="font-semibold mb-1">Progress</h3>
            <ul tw="mb-4 text-sm text-gray-600 dark:text-gray-300 max-h-32 overflow-y-auto">
              {logs.map((log, i) => <li key={i}>{log}</li>)}
            </ul>
            {result && <div tw="mb-2 text-green-700 dark:text-green-400 font-semibold whitespace-pre-line">{result}</div>}
            {error && <div tw="mb-2 text-red-600 font-semibold">Error: {error}</div>}
            <button tw="mt-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded font-semibold" onClick={() => setModalOpen(false)}>Close</button>
          </ModalBox>
        </ModalBg>
      )}
    </div>
  );
} 